// Persistent client-side cache for route preview photos.
//
// Why: route photos can come from a few sources — bundled static assets
// (already cached by Vite + the browser), Supabase storage URLs (cached
// by the browser HTTP cache, but evictable), or future Google Places
// `photo_reference` lookups (signed URLs that change every request and
// would otherwise re-download every page view). This module gives all of
// them ONE consistent fast path:
//
//   1. In-memory Map (instant, survives within the SPA session).
//   2. IndexedDB blob store (survives across reloads, ~50MB budget).
//   3. Network fetch → store in both layers → return object URL.
//
// Static asset URLs (data:/blob:/imported chunks under /assets/) are
// passed through untouched — the bundler+browser already cache those
// optimally, so wrapping them just wastes IDB space.

const DB_NAME = "route-photo-cache";
const STORE = "photos";
const DB_VERSION = 1;
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const MAX_ENTRIES = 200; // soft cap, LRU-pruned on write

interface CacheEntry {
  blob: Blob;
  storedAt: number;
  lastUsed: number;
}

const memCache = new Map<string, string>(); // url -> objectURL
const inflight = new Map<string, Promise<string>>(); // dedupe parallel loads

let dbPromise: Promise<IDBDatabase | null> | null = null;
function openDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === "undefined") return Promise.resolve(null);
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

async function idbGet(key: string): Promise<CacheEntry | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => resolve((req.result as CacheEntry | undefined) ?? null);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function idbPut(key: string, entry: CacheEntry): Promise<void> {
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(entry, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

async function idbDelete(key: string): Promise<void> {
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

/** Trim to MAX_ENTRIES, evicting least-recently-used. Cheap: single full scan. */
async function pruneIfNeeded(): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      const req = store.getAllKeys();
      req.onsuccess = async () => {
        const keys = req.result as IDBValidKey[];
        if (keys.length <= MAX_ENTRIES) { resolve(); return; }
        const all: Array<{ key: IDBValidKey; lastUsed: number }> = [];
        for (const k of keys) {
          await new Promise<void>((r) => {
            const g = store.get(k);
            g.onsuccess = () => {
              const v = g.result as CacheEntry | undefined;
              if (v) all.push({ key: k, lastUsed: v.lastUsed });
              r();
            };
            g.onerror = () => r();
          });
        }
        all.sort((a, b) => a.lastUsed - b.lastUsed);
        const toDrop = all.slice(0, all.length - MAX_ENTRIES);
        for (const { key } of toDrop) store.delete(key);
        resolve();
      };
      req.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

/** Return true for URLs that already get optimal caching from the
 *  bundler / browser and don't need our blob layer. */
function isPassthrough(url: string): boolean {
  if (!url) return true;
  if (url.startsWith("data:") || url.startsWith("blob:")) return true;
  // Vite-emitted static assets and same-origin /assets/* are bundle-hashed
  // and cached forever by the browser already.
  if (url.startsWith("/assets/") || url.includes("/assets/")) {
    try {
      const u = new URL(url, window.location.origin);
      if (u.origin === window.location.origin && u.pathname.includes("/assets/")) {
        return true;
      }
    } catch { /* fall through */ }
  }
  return false;
}

/**
 * Resolve a photo URL through the cache. Returns a stable string suitable
 * for `<img src>`. For passthrough URLs that is the URL itself; for
 * cached ones it is an object URL backed by the IDB blob.
 *
 * Safe to call repeatedly with the same URL — dedupes in-flight fetches.
 */
export async function getCachedPhotoUrl(url: string): Promise<string> {
  if (isPassthrough(url)) return url;

  const hit = memCache.get(url);
  if (hit) return hit;

  const flight = inflight.get(url);
  if (flight) return flight;

  const promise = (async () => {
    // 1) IDB layer.
    const stored = await idbGet(url);
    if (stored && Date.now() - stored.storedAt < TTL_MS) {
      stored.lastUsed = Date.now();
      void idbPut(url, stored); // fire-and-forget LRU bump
      const obj = URL.createObjectURL(stored.blob);
      memCache.set(url, obj);
      return obj;
    }
    if (stored) void idbDelete(url); // expired

    // 2) Network.
    const res = await fetch(url, { cache: "force-cache" });
    if (!res.ok) throw new Error(`Photo fetch failed: ${res.status}`);
    const blob = await res.blob();
    const entry: CacheEntry = { blob, storedAt: Date.now(), lastUsed: Date.now() };
    void idbPut(url, entry).then(() => pruneIfNeeded());
    const obj = URL.createObjectURL(blob);
    memCache.set(url, obj);
    return obj;
  })();

  inflight.set(url, promise);
  try {
    return await promise;
  } finally {
    inflight.delete(url);
  }
}

/** Drop everything (useful for tests / "clear cache" UI). */
export async function clearPhotoCache(): Promise<void> {
  for (const obj of memCache.values()) URL.revokeObjectURL(obj);
  memCache.clear();
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}
